/**
 * Job-level verdict card: graded verdict + s588E-framed narrative, the
 * earliest/best/latest inadequacy-date range, and practitioner override-locks
 * for both the verdict and the dates.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Separator } from "@/ui/primitives/separator";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { ChangeHistory } from "../summarization/shared-panels";
import { format, parseISO, isValid } from "date-fns";
import {
  Lock,
  ShieldAlert,
  ShieldQuestion,
  ShieldCheck,
  Dot,
  CalendarClock,
  Scale,
} from "lucide-react";
import { VerdictBadge } from "./chips";
import { COMPLETENESS_CAVEAT } from "./meta";
import type {
  Assessment,
  Confidence,
  DateEstimate,
  InadequacyDate,
  Verdict,
} from "./types";
import type { Id } from "@cvx/_generated/dataModel";

const EMPTY_DATE: DateEstimate = { date: "", evidence: [], confidence: "low" };

const CONFIDENCE_COLOR: Record<Confidence, BadgeColor> = {
  low: "gray",
  medium: "amber",
  high: "emerald",
};

/** Pretty-print an ISO date as "31 Mar 2025", falling back to the raw string. */
function prettyDate(iso?: string): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "d MMM yyyy") : iso;
}

const DATE_CARD_META: Record<
  "earliest" | "bestEstimate" | "latest",
  { label: string; accent: string; highlight: boolean }
> = {
  earliest: { label: "Earliest", accent: "border-border", highlight: false },
  bestEstimate: {
    label: "Best Estimate",
    accent: "border-primary/40 ring-1 ring-primary/15 bg-primary/[0.03]",
    highlight: true,
  },
  latest: { label: "Latest", accent: "border-border", highlight: false },
};

/** A graphical calendar-style card for one date in the inadequacy range. */
function DateCard({
  which,
  est,
}: {
  which: "earliest" | "bestEstimate" | "latest";
  est: DateEstimate;
}) {
  const meta = DATE_CARD_META[which];
  return (
    <div
      className={`min-w-[180px] flex-1 rounded-lg border p-3 ${meta.accent}`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <CalendarClock className="size-3" />
          {meta.label}
        </span>
        {meta.highlight && (
          <Badge color="indigo" variant="soft" size="sm">
            Seeds claim
          </Badge>
        )}
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums">
          {prettyDate(est.date)}
        </span>
        <Badge
          color={CONFIDENCE_COLOR[est.confidence]}
          variant="soft"
          size="sm"
          className="capitalize"
        >
          {est.confidence}
        </Badge>
      </div>
      {est.evidence.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          {est.evidence.map((e, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <Dot className="-ml-1 size-3.5 shrink-0" />
              <span className="leading-snug">{e}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const S588E_META: Record<
  "available" | "not_yet" | "not_applicable",
  { label: string; color: BadgeColor; box: string; icon: typeof ShieldAlert }
> = {
  available: {
    label: "Available",
    color: "red",
    box: "bg-red-50 border-red-200 text-red-900",
    icon: ShieldAlert,
  },
  not_yet: {
    label: "Not Yet Available",
    color: "amber",
    box: "bg-amber-50 border-amber-200 text-amber-900",
    icon: ShieldQuestion,
  },
  not_applicable: {
    label: "Not Engaged",
    color: "gray",
    box: "bg-muted/40 border-border text-foreground",
    icon: ShieldCheck,
  },
};

export function VerdictCard({
  jobId,
  assessment,
  isViewer,
}: {
  jobId: Id<"jobs">;
  assessment: Assessment;
  isViewer?: boolean;
}) {
  const overrideVerdict = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations
        .overrideVerdict,
    ),
  });
  const overrideDate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations
        .overrideInadequacyDate,
    ),
  });
  const clearOverride = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations
        .clearOverride,
    ),
  });

  const canEdit = !isViewer;
  const o = assessment.practitionerOverride;
  const verdictLocked = o?.verdictLocked === true;
  const dateLocked = o?.inadequacyDateLocked === true;

  const [editingVerdict, setEditingVerdict] = useState(false);
  const [verdictDraft, setVerdictDraft] = useState<Verdict>(assessment.verdict);
  const [verdictReason, setVerdictReason] = useState("");
  const [showReasoning, setShowReasoning] = useState(false);

  const [editingDates, setEditingDates] = useState(false);
  const [draft, setDraft] = useState<InadequacyDate>(
    assessment.inadequacyDate ?? {
      earliest: { ...EMPTY_DATE },
      bestEstimate: { ...EMPTY_DATE },
      latest: { ...EMPTY_DATE },
    },
  );
  const [dateReason, setDateReason] = useState("");

  const saveVerdict = async () => {
    try {
      await overrideVerdict.mutateAsync({
        jobId,
        verdict: verdictDraft,
        reason: verdictReason || undefined,
      });
      toast.success("Verdict overridden and locked");
      setEditingVerdict(false);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to override verdict",
      );
    }
  };

  const saveDates = async () => {
    try {
      await overrideDate.mutateAsync({
        jobId,
        inadequacyDate: {
          earliest: normalise(draft.earliest),
          bestEstimate: normalise(draft.bestEstimate),
          latest: normalise(draft.latest),
        },
        reason: dateReason || undefined,
      });
      toast.success("Inadequacy date overridden and locked");
      setEditingDates(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to override dates");
    }
  };

  return (
    <Card className="bg-background">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Adequacy Verdict</CardTitle>
          <VerdictBadge verdict={assessment.verdict} />
          {verdictLocked && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-300 text-amber-700"
            >
              <Lock className="size-3" /> Locked
            </Badge>
          )}
        </div>
        {canEdit && !editingVerdict && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setVerdictDraft(assessment.verdict);
                setEditingVerdict(true);
              }}
            >
              Override Verdict
            </Button>
            {verdictLocked && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  clearOverride.mutate({ jobId, target: "verdict" })
                }
              >
                Clear Lock
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* The verdict is always keyed to what was uploaded — say so explicitly
            rather than letting the reader assume the upload is complete. */}
        <p className="text-xs italic text-muted-foreground">
          {COMPLETENESS_CAVEAT}
        </p>
        {editingVerdict ? (
          <div className="space-y-2 rounded-md border p-3">
            <Select
              value={verdictDraft}
              onValueChange={(v) => setVerdictDraft(v as Verdict)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="adequate">Adequate</SelectItem>
                <SelectItem value="partially_adequate">
                  Partially Adequate
                </SelectItem>
                <SelectItem value="inadequate">Inadequate</SelectItem>
                <SelectItem value="indeterminate">Indeterminate</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Reason (optional)"
              value={verdictReason}
              onChange={(e) => setVerdictReason(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={saveVerdict}
                disabled={overrideVerdict.isPending}
              >
                Save &amp; Lock
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingVerdict(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Headline — the digestible top line */}
            {assessment.headline && (
              <p className="text-[15px] font-medium leading-snug text-foreground">
                {assessment.headline}
              </p>
            )}

            {/* Key findings — digestible bullets */}
            {assessment.keyFindings && assessment.keyFindings.length > 0 && (
              <ul className="space-y-1.5">
                {assessment.keyFindings.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-1.5 text-sm text-foreground"
                  >
                    <Dot className="-ml-1 size-4 shrink-0 text-muted-foreground" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* s588E(4) status callout */}
        {!editingVerdict &&
          (() => {
            const status =
              assessment.s588eStatus ??
              (assessment.s588ePresumptionAvailable ? "available" : "not_yet");
            const m = S588E_META[status];
            const Icon = m.icon;
            return (
              <div className={`rounded-md border p-3 ${m.box}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Icon className="size-4 shrink-0" />
                  <span className="text-sm font-semibold">
                    s588E(4) Presumption of Insolvency
                  </span>
                  <Badge color={m.color} variant="soft" size="sm">
                    {m.label}
                  </Badge>
                </div>
                {assessment.s588eRationale && (
                  <p className="mt-1.5 text-sm leading-relaxed">
                    {assessment.s588eRationale}
                  </p>
                )}
              </div>
            );
          })()}

        {/* Full reasoning — narrative + plain-English legislative analysis */}
        {!editingVerdict &&
          (assessment.narrative ||
            (assessment.legislativeAnalysis?.length ?? 0) > 0) && (
            <div>
              <button
                type="button"
                onClick={() => setShowReasoning((s) => !s)}
                className="text-xs font-medium text-primary hover:underline"
              >
                {showReasoning ? "Show Less" : "Show Full Reasoning"}
              </button>
              {showReasoning && (
                <div className="mt-3 space-y-3">
                  {assessment.narrative && (
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {assessment.narrative}
                    </p>
                  )}
                  {assessment.legislativeAnalysis &&
                    assessment.legislativeAnalysis.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <Scale className="size-3" />
                          Legislative Analysis
                        </h5>
                        {assessment.legislativeAnalysis.map((l, i) => (
                          <div
                            key={i}
                            className="rounded-md border bg-muted/30 p-3"
                          >
                            <div className="text-sm font-semibold">
                              {l.provision}
                            </div>
                            <p className="mt-0.5 text-xs italic text-muted-foreground">
                              {l.whatItMeans}
                            </p>
                            <p className="mt-1.5 text-sm leading-relaxed">
                              {l.application}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

        {/* Inadequacy date range */}
        {(assessment.inadequacyDate || canEdit) && (
          <div className="rounded-md border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold">
                  Date Records Became Inadequate
                </h4>
                {dateLocked && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-300 text-amber-700"
                  >
                    <Lock className="size-3" /> Locked
                  </Badge>
                )}
              </div>
              {canEdit && !editingDates && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDraft(
                        assessment.inadequacyDate ?? {
                          earliest: { ...EMPTY_DATE },
                          bestEstimate: { ...EMPTY_DATE },
                          latest: { ...EMPTY_DATE },
                        },
                      );
                      setEditingDates(true);
                    }}
                  >
                    Override Dates
                  </Button>
                  {dateLocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        clearOverride.mutate({
                          jobId,
                          target: "inadequacyDate",
                        })
                      }
                    >
                      Clear Lock
                    </Button>
                  )}
                </div>
              )}
            </div>

            <p className="mb-3 text-xs text-muted-foreground">
              The best-estimate seeds the presumed-insolvency date for
              insolvent-trading claims.
            </p>

            {editingDates ? (
              <div className="space-y-3">
                {(["earliest", "bestEstimate", "latest"] as const).map((k) => (
                  <DateEditor
                    key={k}
                    label={
                      k === "bestEstimate"
                        ? "Best Estimate"
                        : k === "earliest"
                          ? "Earliest"
                          : "Latest"
                    }
                    est={draft[k]}
                    onChange={(est) => setDraft({ ...draft, [k]: est })}
                  />
                ))}
                <Input
                  placeholder="Reason (optional)"
                  value={dateReason}
                  onChange={(e) => setDateReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={saveDates}
                    disabled={overrideDate.isPending}
                  >
                    Save &amp; Lock
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingDates(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : assessment.inadequacyDate ? (
              <div className="flex flex-wrap gap-3">
                <DateCard
                  which="earliest"
                  est={assessment.inadequacyDate.earliest}
                />
                <DateCard
                  which="bestEstimate"
                  est={assessment.inadequacyDate.bestEstimate}
                />
                <DateCard
                  which="latest"
                  est={assessment.inadequacyDate.latest}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No inadequacy date estimated (records assessed as adequate).
              </p>
            )}
          </div>
        )}

        {assessment.changeHistory.length > 0 && (
          <>
            <Separator />
            <ChangeHistory
              history={assessment.changeHistory}
              title="Verdict & Date History"
              persistId="books-records-verdict:history"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function normalise(est: DateEstimate): DateEstimate {
  return {
    date: est.date || undefined,
    evidence: est.evidence.filter((e) => e.trim().length > 0),
    confidence: est.confidence,
  };
}

function DateEditor({
  label,
  est,
  onChange,
}: {
  label: string;
  est: DateEstimate;
  onChange: (est: DateEstimate) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_140px]">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            {label}
          </label>
          <DatePicker
            value={est.date || undefined}
            onChange={(v) => onChange({ ...est, date: v })}
            placeholder="Pick a date"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Confidence
          </label>
          <Select
            value={est.confidence}
            onValueChange={(v) =>
              onChange({ ...est, confidence: v as Confidence })
            }
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Textarea
        className="min-h-[44px] text-xs"
        placeholder="Evidence (one per line)"
        value={est.evidence.join("\n")}
        onChange={(e) =>
          onChange({ ...est, evidence: e.target.value.split("\n") })
        }
      />
    </div>
  );
}
